import { useEffect, useId, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  CalendarClock,
  Check,
  CreditCard,
  Download,
  HardDrive,
  Hash,
  MessageCircle,
  ScanFace,
  ShieldCheck,
  Sparkles,
  Tag,
  UploadCloud,
  Wallet,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import AppComingSoon from "@/components/AppComingSoon";
import CompareFeatures from "@/components/pricing2026/CompareFeatures";
import TransactionFeesInfo from "@/components/TransactionFeesInfo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ON_DEMAND_FTP_PATH } from "@/content/onDemandFtp";
import {
  LOWEST_RATE,
  NEW_PLANS,
  PRELAUNCH,
  PRICING_PATHS,
  STORAGE_PACKS,
  WORKED_EXAMPLE,
  newPlan,
  type NewPlan,
  type NewPlanId,
} from "@/content/pricing/plans";
import { fill, formatCount, formatStorage, formatUsd, usePricingCopy, type PricingLocale } from "@/content/pricing";
import { cheapestPackMix } from "@/lib/pricing/changes2026";
import { formatMoney, orderBreakdown, ratePercent } from "@/lib/pricing/orderFee";

// Pricing from 1 November 2026, at /pricing (launched 2 November 2026; the preview address /pricing-2026 301s here
// via LEGACY_PATHS). The home page's PricingSection reuses PlanCard. The /pricing offers (structuredData.ts) are
// NEW_PLANS; it's offered in the eight site languages (seo/pages.ts), the languages this page's copy has.
//
// Copy: src/content/pricing/copy.<lang>.ts (en, es, pt, de, it, ja, zh, ms; anything else falls back to English).
// Numbers: src/content/pricing/plans.ts.

const FEATURE_ICONS: Record<string, LucideIcon> = {
  selfie: ScanFace,
  bib: Hash,
  preview: ShieldCheck,
  whatsapp: MessageCircle,
  uploader: UploadCloud,
};

const CARD_FEE_ICONS: Record<string, LucideIcon> = {
  stripe: CreditCard,
  managed: Wallet,
};

/** Storage examples under the packs: what's billed for a few typical amounts stored (GB). */
const STORAGE_EXAMPLES: { plan: NewPlanId; storedGb: number }[] = [
  { plan: "free", storedGb: 300 },
  { plan: "pro", storedGb: 1400 },
  { plan: "pro", storedGb: 1800 },
  { plan: "max", storedGb: 9000 },
];

const PlanItem = ({ children, featured }: { children: ReactNode; featured: boolean }) => (
  <li className="flex items-start gap-3">
    <span
      className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
        featured ? "bg-hawk-orange" : "bg-hawk-orange/10"
      }`}
    >
      <Check size={12} className={featured ? "text-white" : "text-hawk-orange"} />
    </span>
    <span className={`text-sm ${featured ? "text-white/90" : ""}`}>{children}</span>
  </li>
);

const PlanSection = ({
  icon: Icon,
  label,
  muted,
  children,
}: {
  icon: LucideIcon;
  label: string;
  muted: string;
  children: ReactNode;
}) => (
  <div className="mb-4">
    <div className="mb-2 flex items-center gap-1.5">
      <Icon size={14} className="text-hawk-orange" />
      <span className={`text-xs font-semibold uppercase tracking-wide ${muted}`}>{label}</span>
    </div>
    <ul className="space-y-2">{children}</ul>
  </div>
);

export const PlanCard = ({ plan, loc }: { plan: NewPlan; loc: PricingLocale }) => {
  const { copy, numberLocale } = loc;
  const c = copy.pricing.plans;
  const featured = !!plan.featured;
  const muted = featured ? "text-white/70" : "text-muted-foreground";
  return (
    <Card variant={featured ? "pricingFeatured" : "pricing"} className="relative flex flex-col">
      {featured && (
        <div className="absolute -top-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-to-r from-hawk-orange to-hawk-gold px-4 py-1.5 text-sm font-semibold text-white">
          <Sparkles size={14} />
          {copy.common.mostPopular}
        </div>
      )}
      <CardHeader className="pb-4">
        <CardTitle className={`text-lg ${featured ? "text-white" : ""}`}>{plan.name}</CardTitle>
        <div className="mt-4">
          <span className={`text-4xl font-display font-bold ${featured ? "text-white" : ""}`}>
            {formatUsd(plan.priceUsd, numberLocale)}
          </span>
          <span className={`ml-1 text-sm ${muted}`}>{copy.common.perMonth}</span>
        </div>
        <p className={`mt-2 text-sm ${muted}`}>{copy.common.planTaglines[plan.id]}</p>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col">
        <PlanSection icon={Tag} label={c.sellingLabel} muted={muted}>
          <PlanItem featured={featured}>
            <strong>{fill(c.sellingLine, { rate: ratePercent(plan.commissionRate, numberLocale) })}</strong>
            <span className={`block text-xs ${muted}`}>{c.sellingNote}</span>
          </PlanItem>
        </PlanSection>

        <PlanSection icon={HardDrive} label={c.storageLabel} muted={muted}>
          <PlanItem featured={featured}>
            <strong>{fill(c.storageLine, { storage: formatStorage(plan.storageGb, copy, numberLocale) })}</strong>
            <span className={`block text-xs ${muted}`}>{c.storageNote}</span>
          </PlanItem>
        </PlanSection>

        <PlanSection icon={Download} label={c.freeDownloadsLabel} muted={muted}>
          <PlanItem featured={featured}>
            <strong>{fill(c.freeDownloadsLine, { count: formatCount(plan.freeDownloads, numberLocale) })}</strong>
            <span className={`block text-xs ${muted}`}>{c.overageLine}</span>
          </PlanItem>
        </PlanSection>

        <div className={`mb-6 border-t pt-4 ${featured ? "border-white/20" : "border-border"}`}>
          <span className={`text-xs font-semibold uppercase tracking-wide ${muted}`}>{c.includedLabel}</span>
          <ul className="mt-2 space-y-2">
            {copy.common.planFeatures[plan.id].map((f) =>
              typeof f === "string" ? (
                <PlanItem key={f} featured={featured}>
                  {f}
                </PlanItem>
              ) : (
                <PlanItem key={f.text} featured={featured}>
                  {f.text}
                  <span className={`block text-xs ${muted}`}>{f.note}</span>
                </PlanItem>
              ),
            )}
          </ul>
        </div>

        <Button variant={featured ? "hero" : "outline"} className="mt-auto w-full" asChild>
          <Link to="/register">
            {c.cta}
            <span className="sr-only"> {fill(c.ctaFor, { plan: plan.name })}</span>
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};

const StorageAndDownloads = ({ loc }: { loc: PricingLocale }) => {
  const { copy, numberLocale } = loc;
  const s = copy.pricing.storage;
  const d = copy.pricing.freeDownloads;
  const storage = (gb: number) => formatStorage(gb, copy, numberLocale);
  return (
    <section className="py-20" aria-labelledby="storage-heading">
      <div className="container mx-auto px-4">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.35fr_1fr]">
          {/* Storage */}
          <div className="rounded-2xl bg-card p-6 shadow-lg md:p-8">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold">
              <HardDrive className="text-white" size={22} />
            </div>
            <h2 id="storage-heading" className="mb-2 text-2xl font-display font-bold md:text-3xl">
              {s.heading}
            </h2>
            <p className="mb-6 text-muted-foreground">{s.subtitle}</p>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.includedHeading}</h3>
                <ul className="divide-y divide-border rounded-xl border border-border">
                  {NEW_PLANS.map((p) => (
                    <li key={p.id} className="flex items-center justify-between px-4 py-3 text-sm">
                      <span className="font-semibold">{p.name}</span>
                      <span className="tabular-nums">{storage(p.storageGb)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.packsHeading}</h3>
                <ul className="divide-y divide-border rounded-xl border border-border">
                  {STORAGE_PACKS.map((pack) => (
                    <li key={pack.id} className="flex items-center justify-between px-4 py-3 text-sm">
                      <span className="font-semibold">+{storage(pack.gb)}</span>
                      <span className="tabular-nums">{fill(s.packPrice, { price: formatCount(pack.priceUsd, numberLocale) })}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.examplesHeading}</h3>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              {STORAGE_EXAMPLES.map(({ plan, storedGb }) => {
                const p = newPlan(plan);
                const mix = cheapestPackMix(storedGb - p.storageGb);
                return (
                  <li key={`${plan}-${storedGb}`} className="flex items-start gap-2">
                    <Check size={14} className="mt-0.5 flex-shrink-0 text-hawk-orange" />
                    <span>
                      {fill(s.example, {
                        plan: p.name,
                        stored: storage(storedGb),
                        over: storage(storedGb - p.storageGb),
                        mix: mix.packs.map((x) => `${x.count} × ${storage(x.pack.gb)}`).join(" + "),
                        price: formatCount(mix.priceUsd, numberLocale),
                      })}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-4 text-xs text-muted-foreground">{s.note}</p>
          </div>

          {/* Free-download galleries */}
          <div className="rounded-2xl bg-card p-6 shadow-lg md:p-8">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold">
              <Download className="text-white" size={22} />
            </div>
            <h2 className="mb-2 text-2xl font-display font-bold md:text-3xl">{d.heading}</h2>
            <p className="mb-6 text-muted-foreground">{d.subtitle}</p>
            <ul className="divide-y divide-border rounded-xl border border-border">
              {NEW_PLANS.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                  <span className="font-semibold">{p.name}</span>
                  <span className="text-right tabular-nums">{fill(d.line, { count: formatCount(p.freeDownloads, numberLocale) })}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-muted-foreground">{d.note}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const WorkedExample = ({ loc }: { loc: PricingLocale }) => {
  const { copy, numberLocale } = loc;
  const e = copy.pricing.example;
  const inputId = useId();
  const [amount, setAmount] = useState<string>(String(WORKED_EXAMPLE.order));
  const cur = WORKED_EXAMPLE.currency;

  const value = Number.parseFloat(amount.replace(",", "."));
  const order = Number.isFinite(value) ? value : 0;
  const rows = NEW_PLANS.map((plan) => ({ plan, b: orderBreakdown(order, plan.commissionRate) }));
  // The order and the card fee don't depend on the plan.
  const b = rows[0].b;
  const money = (minor: number) => formatMoney(minor, cur, numberLocale);

  return (
    <div className="rounded-2xl bg-card p-6 shadow-lg md:p-8">
      <Label htmlFor={inputId} className="text-sm font-semibold">
        {e.orderLabel}
      </Label>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-lg font-semibold text-muted-foreground" aria-hidden="true">
          {cur.prefix}
        </span>
        <Input
          id={inputId}
          type="number"
          inputMode="decimal"
          min={0}
          step={1 / 10 ** cur.digits}
          value={amount}
          onChange={(ev) => setAmount(ev.target.value)}
          className="max-w-[200px] text-lg"
          aria-describedby={`${inputId}-result`}
        />
        <span className="text-sm text-muted-foreground">{cur.code}</span>
      </div>

      <div id={`${inputId}-result`} aria-live="polite" className="mt-6 text-sm">
        <dl className="divide-y divide-border border-b border-border">
          <div className="flex justify-between py-3">
            <dt>{e.order}</dt>
            <dd className="font-semibold tabular-nums">{money(b.order)}</dd>
          </div>
          <div className="flex justify-between gap-4 py-3 text-muted-foreground">
            <dt>
              {e.cardFee}
              <span className="block text-xs">{e.cardFeeNote}</span>
            </dt>
            <dd className="whitespace-nowrap tabular-nums">−{money(b.cardFee)}</dd>
          </div>
        </dl>
        <table className="mt-2 w-full">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="py-3 pr-2 font-semibold">{e.colPlan}</th>
              <th scope="col" className="py-3 pr-2 text-right font-semibold">{e.colCommission}</th>
              <th scope="col" className="py-3 text-right font-semibold">{e.colKeep}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(({ plan, b: row }) => (
              <tr key={plan.id}>
                <th scope="row" className="whitespace-nowrap py-3 pr-2 text-left font-semibold">
                  {fill(e.planCell, { plan: plan.name, rate: ratePercent(plan.commissionRate, numberLocale) })}
                </th>
                <td className="whitespace-nowrap py-3 pr-2 text-right text-muted-foreground tabular-nums">−{money(row.commission)}</td>
                <td className="whitespace-nowrap py-3 text-right text-xl font-display font-bold text-hawk-orange tabular-nums">
                  {money(row.keep)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
        <CreditCard size={16} className="mt-0.5 flex-shrink-0 text-hawk-orange" />
        {e.note}
      </p>
    </div>
  );
};

const Pricing2026 = () => {
  const loc = usePricingCopy();
  const { copy, numberLocale } = loc;
  const p = copy.pricing;
  // Arriving on a section link (e.g. #compare-features from the changes page or the Studio page): marketing pages
  // manage their own scroll, so jump to it once the page has rendered.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView();
  }, [hash]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{p.seo.title}</title>
        <meta name="description" content={p.seo.description} />
        {PRELAUNCH && <meta name="robots" content="noindex, nofollow" />}
      </Helmet>
      <AppHeader variant="marketing" />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-hawk-navy pb-16 pt-32 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Link
                to={PRICING_PATHS.changes}
                className="mb-4 inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-full border border-hawk-gold/60 bg-hawk-gold/10 px-4 py-1.5 text-sm font-medium text-hawk-gold transition-colors hover:bg-hawk-gold/20"
              >
                <CalendarClock size={14} />
                {p.notice}
                <span className="inline-flex items-center gap-1 underline underline-offset-2">
                  {p.noticeLink}
                  <ArrowRight size={14} />
                </span>
              </Link>
              <div>
                <span className="mb-4 inline-block rounded-full bg-hawk-orange/15 px-4 py-1.5 text-sm font-semibold text-hawk-orange">
                  {p.hero.badge}
                </span>
              </div>
              <h1 className="mb-6 text-4xl font-display font-bold md:text-5xl">
                {p.hero.title}{" "}
                <span className="whitespace-nowrap text-gradient-primary">
                  {fill(p.hero.titleHighlight, { rate: ratePercent(LOWEST_RATE, numberLocale) })}
                </span>
              </h1>
              <p className="mx-auto max-w-2xl text-lg text-white/80">{p.hero.subtitle}</p>
              <p className="mx-auto mt-3 max-w-2xl text-base text-white/70" data-testid="pricing-hero-events">{p.hero.events}</p>
            </div>

            {/* The rates */}
            <div className="mx-auto mt-10 max-w-4xl rounded-2xl border border-white/15 bg-white/5 p-6 backdrop-blur md:p-8">
              <div className="grid items-center gap-8 md:grid-cols-[1fr_1.1fr]">
                <div>
                  <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white/70">{p.rate.heading}</h2>
                  <ul className="grid grid-cols-3 gap-3">
                    {NEW_PLANS.map((plan) => (
                      <li key={plan.id} className="rounded-xl bg-white/5 px-2 py-4 text-center ring-1 ring-white/10">
                        <span className="block text-4xl font-display font-bold md:text-5xl">{ratePercent(plan.commissionRate, numberLocale)}</span>
                        <span className="mt-1 block text-sm font-semibold text-white/70">{fill(p.rate.planLabel, { plan: plan.name })}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <ul className="space-y-3">
                  {p.rate.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-hawk-orange">
                        <Check size={12} className="text-white" />
                      </span>
                      <span className="text-white/90">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Plans */}
        <section className="py-20" aria-labelledby="plans-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <h2 id="plans-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">
                {p.plans.heading}
              </h2>
              <p className="text-muted-foreground">{p.plans.subtitle}</p>
            </div>
            <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-3 md:gap-6">
              {NEW_PLANS.map((plan) => (
                <PlanCard key={plan.id} plan={plan} loc={loc} />
              ))}
            </div>
            <p className="mx-auto mt-10 flex max-w-3xl items-start justify-center gap-2 text-center text-sm text-muted-foreground">
              <CalendarClock size={16} className="mt-0.5 flex-shrink-0 text-hawk-orange" />
              <span>{p.plans.planChange}</span>
            </p>
            <p className="mt-4 text-center">
              <a href="#compare-features" className="inline-flex items-center gap-1.5 font-semibold text-hawk-orange hover:underline">
                {p.compare.linkLabel}
                <ArrowRight size={15} aria-hidden="true" />
              </a>
            </p>
          </div>
        </section>

        {/* Standard on every plan */}
        <section className="bg-secondary/30 py-20" aria-labelledby="standard-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <h2 id="standard-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">
                {p.standard.heading}
              </h2>
              <p className="text-muted-foreground">{p.standard.subtitle}</p>
            </div>
            <ul className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-5">
              {p.standard.items.map((f) => {
                const Icon = FEATURE_ICONS[f.id] ?? Check;
                return (
                  <li key={f.id} className="rounded-2xl bg-card p-6 shadow-lg">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold">
                      <Icon className="text-white" size={22} />
                    </div>
                    <h3 className="mb-1 font-display font-bold">{f.title}</h3>
                    <p className="text-sm text-muted-foreground">{f.body}</p>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Every Studio feature, plan by plan */}
        <CompareFeatures loc={loc} />

        <StorageAndDownloads loc={loc} />

        {/* Worked example + how card fees work */}
        <section className="bg-secondary/30 py-20" aria-labelledby="example-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
              <div>
                <h2 id="example-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">
                  {p.example.heading}
                </h2>
                <p className="mb-6 text-muted-foreground">{p.example.subtitle}</p>
                <WorkedExample loc={loc} />
              </div>
              <div>
                <h2 className="mb-3 text-2xl font-display font-bold md:mt-2">{p.cardFees.heading}</h2>
                <p className="mb-6 text-muted-foreground">{p.cardFees.intro}</p>
                <ul className="space-y-4">
                  {p.cardFees.items.map((item) => {
                    const Icon = CARD_FEE_ICONS[item.id] ?? CreditCard;
                    return (
                      <li key={item.id} className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-hawk-orange/10">
                          <Icon size={18} className="text-hawk-orange" />
                        </div>
                        <div>
                          <h3 className="mb-1 font-semibold">{item.title}</h3>
                          <p className="text-sm text-muted-foreground">{item.body}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-4 text-sm">
                  <TransactionFeesInfo label={p.cardFees.linkLabel} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20" aria-labelledby="faq-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <h2 id="faq-heading" className="mb-8 text-center text-3xl font-display font-bold md:text-4xl">
              {p.faq.heading}
            </h2>
            <Accordion type="single" collapsible className="rounded-2xl bg-card px-6 shadow-lg">
              {p.faq.items.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className={i === p.faq.items.length - 1 ? "border-b-0" : undefined}>
                  <AccordionTrigger className="text-left text-base">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    <p>{item.a}</p>
                    {"id" in item && item.id === "ftp" && (
                      <Link to={ON_DEMAND_FTP_PATH} className="mt-3 inline-flex items-center gap-1.5 font-semibold text-hawk-orange hover:underline">
                        {copy.common.onDemandFtpLink}
                        <ArrowRight size={14} aria-hidden="true" />
                      </Link>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <AppComingSoon />
      </main>

      <Footer />
    </div>
  );
};

export default Pricing2026;
